import { ObjectActionIconButton } from "@/object-actions/renderers/ObjectActionIconButton";
import { ObjectActionLabeledButton } from "@/object-actions/renderers/ObjectActionLabeledButton";
import type {
  ActionLabelContext,
  ActionPresentation,
  ObjectActionId,
  ObjectKind,
  ResolvedObjectAction,
} from "@/object-actions/types";
import type { ReactNode } from "react";

export type ObjectActionPaneHeaderRenderer = (
  action: ResolvedObjectAction,
  labelCtx: ActionLabelContext,
) => ReactNode;

export interface ObjectActionPaneHeaderActionsProps {
  actions: ResolvedObjectAction[];
  kind: ObjectKind;
  name?: string;
  customRenderers?: Partial<
    Record<ObjectActionId, ObjectActionPaneHeaderRenderer>
  >;
  /** Trailing kebab — typically left-rail manifest actions. */
  kebabMenu?: ReactNode;
}

const PANE_HEADER_PRESENTATIONS: ReadonlySet<ActionPresentation> = new Set([
  "icon-button",
  "primary-button",
  "secondary-button",
]);

function isPaneHeaderPresentation(
  presentation: ActionPresentation,
): presentation is Extract<
  ActionPresentation,
  "icon-button" | "primary-button" | "secondary-button"
> {
  return PANE_HEADER_PRESENTATIONS.has(presentation);
}

/**
 * Renders registry pane-header actions (`icon-button`, `primary-button`,
 * `secondary-button`). Maps each resolved action to the matching control
 * unless a custom renderer is supplied.
 */
export function ObjectActionPaneHeaderActions({
  actions,
  kind,
  name,
  customRenderers,
  kebabMenu,
}: ObjectActionPaneHeaderActionsProps) {
  const labelCtx: ActionLabelContext = { kind, name };
  const paneHeaderActions = actions.filter((action) =>
    isPaneHeaderPresentation(action.presentation),
  );

  if (paneHeaderActions.length === 0 && !kebabMenu) {
    return null;
  }

  return (
    <>
      {paneHeaderActions.map((action) => {
        const custom = customRenderers?.[action.id];
        if (custom) {
          return (
            <span key={action.id} className="inline-flex shrink-0 items-center">
              {custom(action, labelCtx)}
            </span>
          );
        }

        if (action.presentation === "icon-button") {
          return (
            <ObjectActionIconButton
              key={action.id}
              action={action}
              labelCtx={labelCtx}
              variant="pane-header"
            />
          );
        }

        if (
          action.presentation === "primary-button" ||
          action.presentation === "secondary-button"
        ) {
          return (
            <ObjectActionLabeledButton
              key={action.id}
              action={action}
              labelCtx={labelCtx}
              presentation={action.presentation}
            />
          );
        }

        return null;
      })}
      {kebabMenu ? (
        <span className="inline-flex shrink-0 items-center">{kebabMenu}</span>
      ) : null}
    </>
  );
}
